/**
 * Screen Effects — flash overlay (single + multi-flash), projectile trail rendering
 */

import { Container, Graphics } from 'pixi.js';
import { WORLD, Z } from '../constants';
import type { Projectile } from '../types';
import { KOMA_RIBBON } from '../tanks/gyro/constants';
import type { RibbonPoint } from '../tanks/gyro/constants';
import { lighten } from './shapes';

interface QueuedFlash {
  alpha: number;
  delay: number; // ms from queue start
}

export class ScreenEffects {
  private uiContainer: Container;
  private flash: Graphics;
  private flashAlpha: number = 0;
  private flashColor: number = 0xffffff;

  // Multi-flash queue
  private flashQueue: QueuedFlash[] = [];
  private flashQueueStart: number = 0;
  private flashQueueIndex: number = 0;

  private projGfx: Graphics;
  private worldContainer: Container;
  private currentWind: number = 0;

  constructor(worldContainer: Container, uiContainer: Container) {
    this.worldContainer = worldContainer;
    this.uiContainer = uiContainer;

    // Screen flash overlay (on UI layer so it covers everything)
    this.flash = new Graphics();
    this.flash.zIndex = Z.FLASH;
    this.flash.visible = false;
    this.uiContainer.addChild(this.flash);

    // Projectile graphics (on world container)
    this.projGfx = new Graphics();
    this.projGfx.zIndex = Z.PROJECTILES;
    this.worldContainer.addChild(this.projGfx);
  }

  triggerFlash(color: number, alpha: number): void {
    this.flashColor = color;
    this.flashAlpha = alpha;
    this.flash.visible = true;
  }

  triggerMultiFlash(color: number, maxAlpha: number = 0.25): void {
    this.flashColor = color;
    this.flashQueue = [
      { alpha: maxAlpha, delay: 0 },
      { alpha: maxAlpha * 0.5, delay: 80 },
      { alpha: maxAlpha * 0.25, delay: 160 },
    ];
    this.flashQueueStart = Date.now();
    this.flashQueueIndex = 0;
    // Trigger first flash immediately
    this.flashAlpha = this.flashQueue[0].alpha;
    this.flash.visible = true;
    this.flashQueueIndex = 1;
  }

  renderProjectiles(projectiles: Projectile[], wind: number = 0): void {
    this.currentWind = wind;
    this.projGfx.clear();
    for (const proj of projectiles) {
      this.renderSingleProjectile(proj);
    }
  }

  private renderSingleProjectile(proj: Projectile): void {
    // Skip invisible projectiles (radius 0 = no visual)
    if (proj.radius <= 0) return;

    if (proj.weapon.projectileShape === 'koma') {
      this.renderKomaProjectile(proj);
      return;
    }

    if (proj.weapon.projectileShape === 'star') {
      this.renderStarProjectile(proj);
      return;
    }

    if (proj.weapon.projectileShape === 'arrow') {
      this.renderArrowProjectile(proj);
      return;
    }

    // Trail
    for (let i = 0; i < proj.trail.length; i++) {
      const t = i / proj.trail.length;
      const tp = proj.trail[i];
      this.projGfx.circle(tp.x, tp.y, proj.radius * t * 0.7);
      this.projGfx.fill({ color: proj.color, alpha: t * 0.5 });
    }

    if (proj.weapon.pulseEffect) {
      // Pulsing energy projectile — breathing glow at ~2Hz
      const pulse = Math.sin(Date.now() * 0.0126);  // ~2 full cycles/sec
      const pulseRadius = proj.radius * (1 + pulse * 0.12);  // ±12% size oscillation

      // Outer energy halo (breathes with pulse)
      const haloAlpha = 0.12 + pulse * 0.08;  // 0.04–0.20
      this.projGfx.circle(proj.x, proj.y, pulseRadius * 2.5);
      this.projGfx.fill({ color: proj.color, alpha: haloAlpha });

      // White core (pulsing size)
      this.projGfx.circle(proj.x, proj.y, pulseRadius);
      this.projGfx.fill({ color: 0xffffff });

      // Colored glow ring (pulsing alpha)
      const glowAlpha = 0.4 + pulse * 0.12;  // 0.28–0.52
      this.projGfx.circle(proj.x, proj.y, pulseRadius * 1.6);
      this.projGfx.fill({ color: proj.color, alpha: glowAlpha });
    } else {
      // Standard projectile body
      this.projGfx.circle(proj.x, proj.y, proj.radius);
      this.projGfx.fill({ color: 0xffffff });
      this.projGfx.circle(proj.x, proj.y, proj.radius * 1.5);
      this.projGfx.fill({ color: proj.color, alpha: 0.4 });
    }
  }

  /** Render koma (spinning top) projectile — teardrop body, spin accents, glow */
  private renderKomaProjectile(proj: Projectile): void {
    const r = proj.radius;
    const x = proj.x;
    const y = proj.y;
    const color = proj.color;

    // Ribbon trail (replaces particle spiral)
    this.renderKomaRibbon(proj);

    // Trail — teardrop-shaped trail echoes (taller than wide)
    for (let i = 0; i < proj.trail.length; i++) {
      const t = i / proj.trail.length;
      const tp = proj.trail[i];
      const tr = r * t * 0.6;
      // Elongated trail dot (ellipse via two semicircles)
      this.projGfx.ellipse(tp.x, tp.y, tr * 0.7, tr);
      this.projGfx.fill({ color, alpha: t * 0.35 });
    }

    // Outer glow halo (shaped around koma silhouette)
    this.projGfx.ellipse(x, y - r * 0.1, r * 1.6, r * 1.8);
    this.projGfx.fill({ color, alpha: 0.12 });

    // Koma body — teardrop: bulge on top, point faces down (matches gyro tank shape)
    const bulgeR = r * 0.7;
    const taperY = r;

    // Colored stroke (outer glow pass)
    this.projGfx.moveTo(x, y + taperY);
    this.projGfx.quadraticCurveTo(x + bulgeR * 1.2, y + taperY * 0.2, x + bulgeR, y - bulgeR * 0.4);
    this.projGfx.quadraticCurveTo(x + bulgeR * 1.05, y - bulgeR * 1.1, x, y - bulgeR * 1.1);
    this.projGfx.quadraticCurveTo(x - bulgeR * 1.05, y - bulgeR * 1.1, x - bulgeR, y - bulgeR * 0.4);
    this.projGfx.quadraticCurveTo(x - bulgeR * 1.2, y + taperY * 0.2, x, y + taperY);
    this.projGfx.stroke({ color, width: 3.5, alpha: 0.6 });

    // White core fill
    this.projGfx.moveTo(x, y + taperY);
    this.projGfx.quadraticCurveTo(x + bulgeR * 1.2, y + taperY * 0.2, x + bulgeR, y - bulgeR * 0.4);
    this.projGfx.quadraticCurveTo(x + bulgeR * 1.05, y - bulgeR * 1.1, x, y - bulgeR * 1.1);
    this.projGfx.quadraticCurveTo(x - bulgeR * 1.05, y - bulgeR * 1.1, x - bulgeR, y - bulgeR * 0.4);
    this.projGfx.quadraticCurveTo(x - bulgeR * 1.2, y + taperY * 0.2, x, y + taperY);
    this.projGfx.fill({ color: 0xffffff, alpha: 0.85 });

    // Bright inner stroke (neon wireframe aesthetic)
    this.projGfx.moveTo(x, y + taperY);
    this.projGfx.quadraticCurveTo(x + bulgeR * 1.2, y + taperY * 0.2, x + bulgeR, y - bulgeR * 0.4);
    this.projGfx.quadraticCurveTo(x + bulgeR * 1.05, y - bulgeR * 1.1, x, y - bulgeR * 1.1);
    this.projGfx.quadraticCurveTo(x - bulgeR * 1.05, y - bulgeR * 1.1, x - bulgeR, y - bulgeR * 0.4);
    this.projGfx.quadraticCurveTo(x - bulgeR * 1.2, y + taperY * 0.2, x, y + taperY);
    this.projGfx.stroke({ color, width: 1.5, alpha: 1.0 });

    // Spinning accent marks — 4 dots orbiting the equator (widest part)
    const spinAngle = Date.now() * 0.02; // ~3Hz rotation
    const equatorY = y - bulgeR * 0.4;   // widest point of the bulge
    const equatorR = bulgeR * 1.05;       // orbit radius at equator
    for (let i = 0; i < 4; i++) {
      const a = spinAngle + (i * Math.PI * 2) / 4;
      const dotX = x + Math.cos(a) * equatorR;
      // Flatten Y orbit so dots stay near the equator belt
      const dotY = equatorY + Math.sin(a) * (r * 0.25);
      // Only draw dots on the "front" half (simulate 3D)
      const depth = Math.sin(a);
      if (depth > -0.3) {
        const dotAlpha = 0.4 + depth * 0.4;
        const dotSize = 1.5 + depth * 0.8;
        this.projGfx.circle(dotX, dotY, dotSize);
        this.projGfx.fill({ color, alpha: dotAlpha });
      }
    }
  }

  /** Render fabric ribbon trail behind Koma projectile */
  private renderKomaRibbon(proj: Projectile): void {
    if (!proj.visualState?.ribbon) return;
    const ribbon = proj.visualState.ribbon as RibbonPoint[];
    if (ribbon.length < 3) return;

    const color = proj.color;
    const g = this.projGfx;
    const len = ribbon.length;

    // Compute edge points (left/right) with flutter + taper
    const lefts: { x: number; y: number }[] = [];
    const rights: { x: number; y: number }[] = [];
    const centers: { x: number; y: number; alpha: number }[] = [];

    for (let i = 0; i < len; i++) {
      const pt = ribbon[i];
      const t = len > 1 ? i / (len - 1) : 0;

      // Tangent from neighbors
      let tx: number, ty: number;
      if (i === 0 && len > 1) {
        tx = ribbon[0].x - ribbon[1].x;
        ty = ribbon[0].y - ribbon[1].y;
      } else if (i === len - 1 && len > 1) {
        tx = ribbon[i - 1].x - ribbon[i].x;
        ty = ribbon[i - 1].y - ribbon[i].y;
      } else {
        tx = ribbon[i - 1].x - ribbon[i + 1].x;
        ty = ribbon[i - 1].y - ribbon[i + 1].y;
      }

      // Perpendicular (normalized)
      const tLen = Math.sqrt(tx * tx + ty * ty) || 1;
      const px = -ty / tLen;
      const py = tx / tLen;

      // Flutter offset along perpendicular
      const flutter = pt.flutter;

      // Center position with flutter
      const cx = pt.x + px * flutter;
      const cy = pt.y + py * flutter;

      // Tapered half-width
      const hw = KOMA_RIBBON.HEAD_HALF_WIDTH + (KOMA_RIBBON.TAIL_HALF_WIDTH - KOMA_RIBBON.HEAD_HALF_WIDTH) * t;

      lefts.push({ x: cx + px * hw, y: cy + py * hw });
      rights.push({ x: cx - px * hw, y: cy - py * hw });
      centers.push({ x: cx, y: cy, alpha: 1 - t });
    }

    // Pass 1: Glow fill — polygon from left edge forward + right edge backward
    g.moveTo(lefts[0].x, lefts[0].y);
    for (let i = 1; i < len; i++) g.lineTo(lefts[i].x, lefts[i].y);
    for (let i = len - 1; i >= 0; i--) g.lineTo(rights[i].x, rights[i].y);
    g.closePath();
    g.fill({ color, alpha: 0.08 });
    g.stroke({ color, width: 4, alpha: 0.15 });

    // Pass 2: Neon center stroke — per-segment with fading alpha
    const brightColor = lighten(color, 0.4);
    for (let i = 0; i < len - 1; i++) {
      const alpha = centers[i].alpha * 0.8;
      if (alpha < 0.02) continue;
      g.moveTo(centers[i].x, centers[i].y);
      g.lineTo(centers[i + 1].x, centers[i + 1].y);
      g.stroke({ color: brightColor, width: 1.5, alpha });
    }

    // Pass 3: Hot tip — first 3 segments get white accent
    const tipLen = Math.min(3, len - 1);
    for (let i = 0; i < tipLen; i++) {
      const alpha = (1 - i / tipLen) * 0.6;
      g.moveTo(centers[i].x, centers[i].y);
      g.lineTo(centers[i + 1].x, centers[i + 1].y);
      g.stroke({ color: 0xffffff, width: 0.8, alpha });
    }
  }

  /** Render star-shaped projectile — tiny 4-point star with glow */
  private renderStarProjectile(proj: Projectile): void {
    const r = proj.radius;
    const x = proj.x;
    const y = proj.y;
    const color = proj.color;

    // Trail — tiny star echoes
    for (let i = 0; i < proj.trail.length; i++) {
      const t = i / proj.trail.length;
      const tp = proj.trail[i];
      const tr = r * t * 0.5;
      this.projGfx.circle(tp.x, tp.y, tr);
      this.projGfx.fill({ color, alpha: t * 0.3 });
    }

    // Outer glow
    this.projGfx.circle(x, y, r * 2.5);
    this.projGfx.fill({ color, alpha: 0.1 });

    // 4-point star shape — alternating outer/inner vertices
    const spinAngle = Date.now() * 0.015; // slow spin
    const outerR = r * 1.4;
    const innerR = r * 0.5;
    this.projGfx.moveTo(
      x + Math.cos(spinAngle) * outerR,
      y + Math.sin(spinAngle) * outerR,
    );
    for (let i = 1; i < 8; i++) {
      const a = spinAngle + (i * Math.PI) / 4;
      const pointR = i % 2 === 0 ? outerR : innerR;
      this.projGfx.lineTo(
        x + Math.cos(a) * pointR,
        y + Math.sin(a) * pointR,
      );
    }
    this.projGfx.closePath();
    this.projGfx.fill({ color: 0xffffff, alpha: 0.9 });
    this.projGfx.stroke({ color, width: 1.0, alpha: 0.8 });
  }

  /** Render arrow projectile — elongated shaft oriented along velocity, pointed head, fletching fins */
  private renderArrowProjectile(proj: Projectile): void {
    const color = proj.color;
    const angle = Math.atan2(proj.vy, proj.vx);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    // Arrow dimensions (local space, tip at +X direction)
    const headLen = 14;
    const headW = 7;
    const shaftLen = 28;
    const shaftW = 2;
    const fletchLen = 8;
    const fletchW = 5;

    // Rotate local (lx, ly) into world coords
    const toX = (lx: number, ly: number) => proj.x + lx * cos - ly * sin;
    const toY = (lx: number, ly: number) => proj.y + lx * sin + ly * cos;

    // ── Trail: fading line along past positions ──
    if (proj.trail.length > 1) {
      for (let i = 1; i < proj.trail.length; i++) {
        const t = i / proj.trail.length;
        const p0 = proj.trail[i - 1];
        const p1 = proj.trail[i];
        this.projGfx.moveTo(p0.x, p0.y);
        this.projGfx.lineTo(p1.x, p1.y);
        this.projGfx.stroke({ color, width: 2.5 * t, alpha: t * 0.4 });
      }
      const last = proj.trail[proj.trail.length - 1];
      this.projGfx.moveTo(last.x, last.y);
      this.projGfx.lineTo(proj.x, proj.y);
      this.projGfx.stroke({ color, width: 2.5, alpha: 0.5 });
    }

    // ── Soft glow ──
    this.projGfx.circle(proj.x, proj.y, 16);
    this.projGfx.fill({ color, alpha: 0.1 });

    // ── Arrowhead (colored glow pass) ──
    this.projGfx.moveTo(toX(headLen, 0), toY(headLen, 0));
    this.projGfx.lineTo(toX(-headLen * 0.1, -headW), toY(-headLen * 0.1, -headW));
    this.projGfx.lineTo(toX(0, 0), toY(0, 0));
    this.projGfx.lineTo(toX(-headLen * 0.1, headW), toY(-headLen * 0.1, headW));
    this.projGfx.closePath();
    this.projGfx.fill({ color, alpha: 0.5 });

    // ── Shaft (colored glow pass) ──
    this.projGfx.moveTo(toX(0, -shaftW), toY(0, -shaftW));
    this.projGfx.lineTo(toX(-shaftLen, -shaftW), toY(-shaftLen, -shaftW));
    this.projGfx.lineTo(toX(-shaftLen, shaftW), toY(-shaftLen, shaftW));
    this.projGfx.lineTo(toX(0, shaftW), toY(0, shaftW));
    this.projGfx.closePath();
    this.projGfx.fill({ color, alpha: 0.35 });

    // ── Arrowhead (bright white core) ──
    this.projGfx.moveTo(toX(headLen, 0), toY(headLen, 0));
    this.projGfx.lineTo(toX(-headLen * 0.1, -headW * 0.7), toY(-headLen * 0.1, -headW * 0.7));
    this.projGfx.lineTo(toX(1, 0), toY(1, 0));
    this.projGfx.lineTo(toX(-headLen * 0.1, headW * 0.7), toY(-headLen * 0.1, headW * 0.7));
    this.projGfx.closePath();
    this.projGfx.fill({ color: 0xffffff, alpha: 0.9 });

    // ── Shaft (white core — thinner) ──
    const coreW = shaftW * 0.6;
    this.projGfx.moveTo(toX(0, -coreW), toY(0, -coreW));
    this.projGfx.lineTo(toX(-shaftLen, -coreW), toY(-shaftLen, -coreW));
    this.projGfx.lineTo(toX(-shaftLen, coreW), toY(-shaftLen, coreW));
    this.projGfx.lineTo(toX(0, coreW), toY(0, coreW));
    this.projGfx.closePath();
    this.projGfx.fill({ color: 0xffffff, alpha: 0.7 });

    // ── Fletching fins ──
    const tailX = -shaftLen;
    this.projGfx.moveTo(toX(tailX, 0), toY(tailX, 0));
    this.projGfx.lineTo(toX(tailX - fletchLen, -fletchW), toY(tailX - fletchLen, -fletchW));
    this.projGfx.lineTo(toX(tailX - fletchLen * 0.4, 0), toY(tailX - fletchLen * 0.4, 0));
    this.projGfx.closePath();
    this.projGfx.fill({ color, alpha: 0.6 });
    this.projGfx.moveTo(toX(tailX, 0), toY(tailX, 0));
    this.projGfx.lineTo(toX(tailX - fletchLen, fletchW), toY(tailX - fletchLen, fletchW));
    this.projGfx.lineTo(toX(tailX - fletchLen * 0.4, 0), toY(tailX - fletchLen * 0.4, 0));
    this.projGfx.closePath();
    this.projGfx.fill({ color, alpha: 0.6 });

    // ── Neon wireframe stroke — arrowhead ──
    this.projGfx.moveTo(toX(headLen, 0), toY(headLen, 0));
    this.projGfx.lineTo(toX(-headLen * 0.1, -headW), toY(-headLen * 0.1, -headW));
    this.projGfx.lineTo(toX(0, 0), toY(0, 0));
    this.projGfx.lineTo(toX(-headLen * 0.1, headW), toY(-headLen * 0.1, headW));
    this.projGfx.closePath();
    this.projGfx.stroke({ color, width: 1.5, alpha: 0.8 });

    // ── Neon wireframe stroke — shaft ──
    this.projGfx.moveTo(toX(0, -shaftW), toY(0, -shaftW));
    this.projGfx.lineTo(toX(-shaftLen, -shaftW), toY(-shaftLen, -shaftW));
    this.projGfx.lineTo(toX(-shaftLen, shaftW), toY(-shaftLen, shaftW));
    this.projGfx.lineTo(toX(0, shaftW), toY(0, shaftW));
    this.projGfx.closePath();
    this.projGfx.stroke({ color, width: 1.0, alpha: 0.6 });
  }

  update(dt: number, frozen: boolean = false): void {
    // Process multi-flash queue (always — uses wall-clock time)
    if (this.flashQueueIndex < this.flashQueue.length) {
      const elapsed = Date.now() - this.flashQueueStart;
      const next = this.flashQueue[this.flashQueueIndex];
      if (elapsed >= next.delay && this.flashAlpha <= 0) {
        this.flashAlpha = next.alpha;
        this.flash.visible = true;
        this.flashQueueIndex++;
      }
    }

    // During hitstop, hold the flash at current alpha (don't decay)
    if (frozen) return;

    // Flash decay
    if (this.flashAlpha > 0) {
      this.flashAlpha -= dt * 8; // fade over ~0.12s
      if (this.flashAlpha <= 0) {
        this.flashAlpha = 0;
        this.flash.visible = false;
      } else {
        this.flash.clear();
        this.flash.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
        this.flash.fill({ color: this.flashColor, alpha: this.flashAlpha });
      }
    }
  }

  destroy(): void {
    this.flash.destroy();
    this.projGfx.destroy();
  }
}
